import React, { useEffect, useMemo } from "react";
import { useGarmentMeasurementServiceContext } from "../../../../contexts/GarmentMeasurementServiceContext";
import { useMainScroll } from "../../../../contexts/MainScrollContext";
import GarmentMeasurementServicePagination from "../GarmentMeasurementServicePagination";
import GarmentMeasurementServiceImageList from "../GarmentMeasurementServiceImageList";
import Loader from "../../../loading/Loader";
import InteractiveGarmentMeasurementResult from "../InteractiveGarmentMeasurementResult";
import ExportResults from "../ExportResults";
import UnitToggle from "../../../shared/UnitToggle";

const formatLabel = (key: string): string => {
  if (!key) return "";

  const formatted = key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim();

  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const DisableableWrapper: React.FC<{ children: React.ReactNode; disabled: boolean }> = ({ children, disabled }) => {
  if (!disabled) return <>{children}</>;

  return (
    <div className="relative">
      {children}
      <div className="absolute inset-0 z-10 cursor-not-allowed bg-white/50 bg-opacity-60"></div>
    </div>
  );
};

const MeasurementResult: React.FC = () => {
  const {
    garmentMeasurementResult,
    allMeasurements,
    convertedMeasurements,
    currentImageIndex,
    updateMeasurementForCurrentImage,
    measurementUnit,
    toggleMeasurementUnit,
    getCurrentMeasurementError,
    loading,
  } = useGarmentMeasurementServiceContext();
  const { scrollToTop } = useMainScroll();

  const measurementItems = useMemo(() => {
    if (!convertedMeasurements) return [];

    const items = [];

    for (const key in convertedMeasurements) {
      if (key === "Details" || key === "MetaData") continue;

      const value = convertedMeasurements[key];
      if (value === undefined || value === 0) continue;

      const label = formatLabel(key);

      items.push({
        key,
        label,
        value,
      });
    }

    return items.sort((a, b) => {
      if (a.key.includes("Top") && !b.key.includes("Top")) return -1;
      if (!a.key.includes("Top") && b.key.includes("Top")) return 1;

      if (a.key.includes("Bottom") && !b.key.includes("Bottom")) return 1;
      if (!a.key.includes("Bottom") && b.key.includes("Bottom")) return -1;

      return 0;
    });
  }, [convertedMeasurements]);

  useEffect(() => {
    updateMeasurementForCurrentImage();
    scrollToTop();
  }, [currentImageIndex]);

  if (loading) {
    return (
      <div className="flex justify-center w-full p-8">
        <Loader />
      </div>
    );
  }

  const hasMeasurements = measurementItems.length > 0;
  const measurementError = getCurrentMeasurementError?.();

  const renderMeasurementsOrError = () => {
    if (measurementError) {
      return <div className="py-4 text-center text-closed-shutter-400">Error: {measurementError}</div>;
    }

    if (!hasMeasurements) {
      return <div className="py-4 text-center text-gorgonzola-blue-400">No measurements available for this image</div>;
    }

    return null;
  };

  const safeGarmentResult = garmentMeasurementResult || {
    Details: {},
    MetaData: { Date: new Date().toISOString().split("T")[0], FileNames: [] },
  };

  return (
    <div className="flex flex-col items-center w-full gap-8 md:gap-16 ">
      <div className="hidden max-w-5xl border rounded-lg w-fit md:flex md:gap-4 md:flex-col md:items-center border-gorgonzola-blue-50 ">
        <div className="flex justify-center gap-4">
          <div className="p-4 w-fit">
            <InteractiveGarmentMeasurementResult />
          </div>

          <div className="flex-1 h-full flex flex-col items-center p-4 max-w-[30rem] xl:min-w-[30rem]">
            <div className="flex justify-center w-full mb-3">
              <DisableableWrapper disabled={!hasMeasurements}>
                <UnitToggle selectedUnit={measurementUnit} onUnitChange={toggleMeasurementUnit} />
              </DisableableWrapper>
            </div>
            <div className="grid grid-cols-1 gap-4 overflow-y-auto max-h-[25rem] custom_scroll relative">
              {renderMeasurementsOrError() ||
                measurementItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between gap-4 p-4 border rounded-lg min-w-64 border-gorgonzola-blue-50">
                    <span className="text-xs font-medium text-gorgonzola-blue-800">{item.label}</span>
                    <div className="flex items-end min-w-24">
                      <span className="font-bold text-gorgonzola-blue-600">{item.value}</span>
                      <span className="ml-1 text-xs text-gorgonzola-blue-400">{measurementUnit}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <div className="w-full px-6 py-4">
          <DisableableWrapper disabled={!hasMeasurements}>
            <ExportResults measurements={safeGarmentResult} fileName={`garment-measurements-${currentImageIndex + 1}`} allMeasurements={allMeasurements} />
          </DisableableWrapper>
        </div>
      </div>

      <div className="flex flex-col w-full gap-10 md:hidden">
        <div className="flex flex-col gap-4">
          <div className="flex justify-center p-4 border rounded-lg border-gorgonzola-blue-50">
            <InteractiveGarmentMeasurementResult />
          </div>

          <div className="flex flex-col items-center w-full gap-4">
            <div className="flex justify-center w-full mb-3">
              <DisableableWrapper disabled={!hasMeasurements}>
                <UnitToggle selectedUnit={measurementUnit} onUnitChange={toggleMeasurementUnit} />
              </DisableableWrapper>
            </div>
            <div className="grid grid-cols-1 gap-3 overflow-y-auto max-h-[25rem] custom_scroll relative">
              {renderMeasurementsOrError() ||
                measurementItems.map((item, index) => (
                  <div key={index} className="flex flex-col p-4 border rounded-lg border-gorgonzola-blue-50">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-gorgonzola-blue-800">{item.label}</span>
                      <div className="flex items-end min-w-24">
                        <span className="font-bold text-gorgonzola-blue-600">{item.value}</span>
                        <span className="ml-1 text-xs text-gorgonzola-blue-400">{measurementUnit}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <DisableableWrapper disabled={!hasMeasurements}>
          <ExportResults measurements={safeGarmentResult} fileName={`garment-measurements-${currentImageIndex + 1}`} allMeasurements={allMeasurements} />
        </DisableableWrapper>
      </div>

      <div className="flex flex-col w-full max-w-5xl border divide-y rounded-lg border-gorgonzola-blue-50 divide-gorgonzola-blue-50 ">
        <h3 className="p-4 text-closed-shutter-300">All Uploaded Images</h3>
        <div className="p-4">
          <GarmentMeasurementServiceImageList showMeasurements={true} />
        </div>
      </div>

      <GarmentMeasurementServicePagination handlerButtonText="Try Again" isMeasurementSuccessed={true} />
    </div>
  );
};

export default MeasurementResult;
